import React from 'react';
import { Box, Copy, Icon, Optional, Media, Column } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import themeStyles from './AdditionalWarranty.styles';
import AdditionalWarrantyCard from './AdditionalWarrantyCard';
import useAdditionalWarrantyFetch from './useAdditionalWarrantyFetch';
import { useWarranty } from '../SelectProductsContext/SelectProductsProvider';
import BuildWrapper from '../BuildWrapper';
import MobilePagination from './MobilePagination';
import ConfigurationProvider from './service/ConfigurationProvider';
import Slides from './Slides';
import LoadingState from '../Accessories/shared/LoadingState';
import pushGtmWarrantySelectEvent from './service/utils';
import getDropdownOptionsData, { optionsKeys } from '../../utils/getDropdownOptionsData';
import useDropdownOptions from '../../hooks/useDropdownOptions';

const Container = themeStyles.apply(Box, 'Container');
const LogoContent = themeStyles.apply(Box, 'LogoContent');
const Separator = themeStyles.apply(Box, 'Separator');
const Cards = themeStyles.apply(Box, 'Cards');
const ErrorCopy = themeStyles.apply(Copy, 'ErrorCopy');
const SliderContainer = themeStyles.apply(Column, 'SliderContainer');
const WarrantyDisclaimer = themeStyles.apply(Copy, 'WarrantyDisclaimer');

const AdditionalWarranty = ({ selectedTrimId }) => {
  const { t } = useTranslation();

  const { warranties, warrantiesLength, hasError, isFetching } = useAdditionalWarrantyFetch(selectedTrimId);

  const [warranty, setWarranty] = useWarranty();

  const { dropdownOptions } = useDropdownOptions();
  const [yearData, modelData, transmissionData] = getDropdownOptionsData(dropdownOptions, [
    optionsKeys.year,
    optionsKeys.model,
    optionsKeys.transmission,
  ]);

  const modelName = modelData?.text;
  const modelYear = yearData?.value;
  const modelTransmission = transmissionData?.value;

  const onClickEvent = businessKey => {
    if (warranty === businessKey) {
      setWarranty('');
    } else {
      setWarranty(businessKey);
    }
  };

  return (
    <ConfigurationProvider>
      <Container>
        <Optional when={!isFetching}>
          <LogoContent data-testid="cy-logo-content">
            <Icon name="horizontalLogo" />
            <Separator as="span" />
            <Icon name="plusWord" />
          </LogoContent>
        </Optional>
        <Optional when={isFetching}>
          <LoadingState />
        </Optional>
        <WarrantyDisclaimer size="extraSmall" data-testid="cy-warranty-disclaimer">
          {t('Sub-apps.PriceAndFinance.warrantyDisclaimerQCLabel')}
        </WarrantyDisclaimer>
        <Optional when={warrantiesLength}>
          <Media greaterThanOrEqual="desktop">
            <Cards isSingle={warrantiesLength === 1}>
              {warranties?.map(item => (
                <BuildWrapper key={item.businessKey} isOnBuild={warranty === item.businessKey}>
                  <AdditionalWarrantyCard
                    title={t('Sub-apps.PriceAndFinance.hondaPlusLabel')}
                    name={item.name}
                    price={item.price}
                    isSelected={warranty === item.businessKey}
                    onClickEvent={() => {
                      onClickEvent(item.businessKey);
                      pushGtmWarrantySelectEvent(item.gtmName, `${modelYear}: ${modelTransmission}: ${modelName}`);
                    }}
                    isSingle={warrantiesLength === 1}
                  />
                </BuildWrapper>
              ))}
            </Cards>
          </Media>
          <Media lessThan="desktop">
            <SliderContainer>
              <Slides warranties={warranties} />
            </SliderContainer>
          </Media>
        </Optional>

        <Optional when={!isFetching && !hasError && !warrantiesLength}>
          <ErrorCopy isNoWarranty data-testid="cy-empty-warranties">
            {t('Sub-apps.PriceAndFinance.noWarrantiesLabel')}
          </ErrorCopy>
        </Optional>

        <Optional when={hasError}>
          <ErrorCopy data-testid="cy-api-fails">
            <Icon name="information" />
            {t('Sub-apps.PriceAndFinance.unableToLoadError')}
          </ErrorCopy>
        </Optional>
      </Container>
      <Optional when={warrantiesLength}>
        <Media lessThan="desktop">
          <MobilePagination length={warrantiesLength} />
        </Media>
      </Optional>
    </ConfigurationProvider>
  );
};

AdditionalWarranty.propTypes = {
  selectedTrimId: PropTypes.string,
};

export default AdditionalWarranty;
